if (!customElements.get("tabbed-hot-products")) {
    class TabbedHotProducts extends HTMLElement {
        constructor() {
            super();

            this.tabs = [];
            this.panels = [];

            this.onClick = this.handleClick.bind(this);
            this.onKeyDown = this.handleKeyDown.bind(this);
            this.onBlockSelect = this.handleBlockSelect.bind(this);
        }

        connectedCallback() {
            this.tabList = this.querySelector("[data-tabbed-tabs]");
            this.tabScroll = this.querySelector("[data-tabbed-tab-scroll]");
            this.panels = Array.from(this.querySelectorAll("[data-tabbed-panel]"));
            this.viewAll = this.querySelector("[data-tabbed-view-all]");

            if (!this.tabList || this.panels.length === 0) return;

            this.preparePanelContent();
            this.buildTabs();

            this.tabs.forEach((tab) => {
                tab.addEventListener("click", this.onClick);
                tab.addEventListener("keydown", this.onKeyDown);
            });

            if (window.Shopify?.designMode) {
                document.addEventListener(
                    "shopify:block:select",
                    this.onBlockSelect
                );
            }

            this.activateTab(this.tabs[0], false);
            this.classList.add("is-ready");
        }

        disconnectedCallback() {
            this.tabs.forEach((tab) => {
                tab.removeEventListener("click", this.onClick);
                tab.removeEventListener("keydown", this.onKeyDown);
            });
            document.removeEventListener(
                "shopify:block:select",
                this.onBlockSelect
            );
        }

        get isRtl() {
            return getComputedStyle(this).direction === "rtl";
        }

        buildTabs() {
            this.tabList.replaceChildren();

            this.tabs = this.panels.map((panel, index) => {
                const tab = document.createElement("button");
                const tabId = `TabbedHotProductsTab-${panel.dataset.panelId}`;

                tab.id = tabId;
                tab.className = "tabbed-hot-products__tab";
                tab.type = "button";
                tab.role = "tab";
                tab.tabIndex = index === 0 ? 0 : -1;
                tab.setAttribute("aria-selected", String(index === 0));
                tab.setAttribute("aria-controls", panel.id);
                tab.dataset.tabbedTab = "";
                tab.dataset.tabId = panel.dataset.panelId;
                tab.dataset.viewAllUrl = panel.dataset.viewAllUrl || "";
                tab.textContent = panel.dataset.tabTitle || "Category";

                panel.setAttribute("aria-labelledby", tabId);
                this.tabList.append(tab);
                return tab;
            });

            this.tabScroll.hidden = this.tabs.length === 0;
        }

        preparePanelContent() {
            this.panels.forEach((panel) => {
                if (panel.dataset.contentSource !== "automatic") return;

                const automaticGrid = panel.querySelector(
                    "[data-tabbed-automatic-grid]"
                );
                const nestedGrid = panel.querySelector("[data-tabbed-nested-grid]");
                if (!automaticGrid || !nestedGrid) return;

                const allCard = automaticGrid.querySelector("[data-tabbed-all-card]");
                const insertCard = (card) => {
                    automaticGrid.insertBefore(card, allCard || null);
                };

                Array.from(nestedGrid.children).forEach((card) => {
                    const childType = card.dataset.tabbedChildType;

                    if (childType === "custom") {
                        insertCard(card);
                        return;
                    }

                    if (childType !== "product") return;

                    const productId = card.dataset.tabbedProductId;
                    const automaticCard = productId
                        ? automaticGrid.querySelector(
                              `[data-tabbed-product-id="${CSS.escape(productId)}"]`
                          )
                        : null;

                    if (automaticCard) automaticCard.replaceWith(card);
                });

                nestedGrid.hidden = true;
            });
        }

        handleClick(event) {
            this.activateTab(event.currentTarget, false);
        }

        handleKeyDown(event) {
            const currentIndex = this.tabs.indexOf(event.currentTarget);
            let targetIndex = currentIndex;

            if (event.key === "Home") {
                targetIndex = 0;
            } else if (event.key === "End") {
                targetIndex = this.tabs.length - 1;
            } else if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                const nextKey = this.isRtl ? "ArrowLeft" : "ArrowRight";
                const step = event.key === nextKey ? 1 : -1;
                targetIndex =
                    (currentIndex + step + this.tabs.length) % this.tabs.length;
            } else {
                return;
            }

            event.preventDefault();
            this.activateTab(this.tabs[targetIndex], true);
        }

        handleBlockSelect(event) {
            const blockId = event.detail?.blockId;
            let selectedTab = this.tabs.find(
                (tab) =>
                    tab.dataset.tabId === blockId
            );

            if (!selectedTab) {
                const selectedPanel = event.target?.closest?.(
                    "[data-tabbed-panel]"
                );
                const selectedContent = blockId
                    ? this.querySelector(
                          `[data-tab-content-block="${CSS.escape(blockId)}"]`
                      )
                    : null;
                const parentPanel =
                    selectedPanel || selectedContent?.closest("[data-tabbed-panel]");
                selectedTab = this.tabs.find(
                    (tab) => tab.dataset.tabId === parentPanel?.dataset.panelId
                );
            }

            if (selectedTab) this.activateTab(selectedTab, false, true);
        }

        activateTab(activeTab, moveFocus, scrollTab = moveFocus) {
            if (!activeTab) return;

            const activeId = activeTab.dataset.tabId;
            this.tabs.forEach((tab) => {
                const isActive = tab === activeTab;
                tab.classList.toggle("is-active", isActive);
                tab.setAttribute("aria-selected", String(isActive));
                tab.tabIndex = isActive ? 0 : -1;
            });

            this.panels.forEach((panel) => {
                panel.hidden = panel.dataset.panelId !== activeId;
            });

            if (this.viewAll) {
                const viewAllUrl = activeTab.dataset.viewAllUrl;
                this.viewAll.hidden = !viewAllUrl;
                if (viewAllUrl) {
                    this.viewAll.href = viewAllUrl;
                } else {
                    this.viewAll.removeAttribute("href");
                }
            }

            if (scrollTab) {
                activeTab.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "nearest",
                });
            }
            if (moveFocus) activeTab.focus();
        }
    }

    customElements.define("tabbed-hot-products", TabbedHotProducts);
}
